import axios from 'axios'
import { useAuthStore } from '@/stores/auth'
import router from '@/router'
import { toast } from 'vue3-toastify'

// Use relative URL if VITE_API_URL is not set (for Docker/nginx proxy)
// Otherwise use the provided URL (for local development)
const API_BASE_URL = import.meta.env.VITE_API_URL !== undefined && import.meta.env.VITE_API_URL !== ''
  ? import.meta.env.VITE_API_URL
  : ''

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL ? `${API_BASE_URL}/api/v1` : '/api/v1',
  timeout: 120000,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  }
})

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const authStore = useAuthStore()
    if (authStore.token) {
      config.headers.Authorization = `Bearer ${authStore.token}`
    }

    // For FormData requests, let browser set Content-Type with boundary
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type']
    }

    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => {
    return response.data
  },
  async (error) => {
    const authStore = useAuthStore()
    const originalRequest = error.config

    // Prevent infinite loop on refresh endpoint
    if (error.response?.status === 401 && !originalRequest._retry && !originalRequest.url.includes('/auth/refresh')) {
      originalRequest._retry = true

      // Try to refresh token
      const refreshed = await authStore.refreshToken()

      if (refreshed) {
        // Retry the original request
        originalRequest.headers.Authorization = `Bearer ${authStore.token}`
        return api.request(originalRequest)
      } else {
        // Refresh failed, logout and redirect
        authStore.clearAuthData()
        router.push('/login')
        toast.error('Sesi Anda telah berakhir, silakan login kembali')
      }
    }

    // Handle other HTTP errors
    if (error.response?.status === 403) {
      toast.error('Anda tidak memiliki izin untuk mengakses resource ini')
    } else if (error.response?.status === 404) {
      toast.error('Resource tidak ditemukan')
    } else if (error.response?.status === 422) {
      // Validation errors are handled by individual components
      return Promise.reject(error)
    } else if (error.response?.status >= 500) {
      toast.error('Terjadi kesalahan server, silakan coba lagi nanti')
    } else if (error.code === 'ECONNABORTED') {
      toast.error('Request timeout, silakan coba lagi')
    } else if (!error.response) {
      toast.error('Tidak dapat terhubung ke server')
    }

    return Promise.reject(error)
  }
)

// AI Service API
const aiApi = axios.create({
  baseURL: import.meta.env.VITE_AI_API_URL || 'http://localhost:8000',
  timeout: 60000, // Longer timeout for AI operations
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  }
})

// AI API request interceptor
aiApi.interceptors.request.use(
  (config) => {
    // Add AI service token if available
    const aiToken = import.meta.env.VITE_AI_API_TOKEN
    if (aiToken) {
      config.headers.Authorization = `Bearer ${aiToken}`
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// AI API response interceptor
aiApi.interceptors.response.use(
  (response) => response.data,
  (error) => {
    if (error.code === 'ECONNABORTED') {
      toast.error('AI service timeout, silakan coba lagi')
    } else if (!error.response) {
      toast.error('Tidak dapat terhubung ke AI service')
    }
    return Promise.reject(error)
  }
)

export { api, aiApi }
export default api